import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import { debounce } from "lodash";
import { toast } from "react-hot-toast";
import {
  MagnifyingGlassIcon,
  XMarkIcon,
  ChevronDownIcon,
  ExclamationCircleIcon,
  FolderIcon,
} from "@heroicons/react/24/outline";
import { useDispatch, useSelector } from "react-redux";
import { SelectorListSkeleton } from "../ResourceLoading";
import { PMDialog } from "../PMDialog";
import { useAutofocusOnOpen } from "../useAutofocusOnOpen";
import {
  createProjectFolderAsync,
  deleteProjectFolderAsync,
  getProjectFoldersAsync,
  updateProjectFolderAsync,
} from "../../../slices/project/folder";
import { ProjectFolderTree } from "./ProjectFolderTree";
import {
  buildFolderTree,
  countDescendants,
  findNode,
  flatten,
  namePath,
  searchRows,
  ancestorPath,
  isDescendant,
} from "./projectFolderTreeUtils";

export const ProjectFolderSelector = (props) => {
  const {
    setFieldValue,
    values,
    errors,
    onChange,
    multiSelect = false,
    allowManage = true,
    projectTitle: projectTitleProp,
    project: projectProp,
    noneLabelKey,
  } = props;

  const name = "folder_id";
  const label = props?.label ?? "Folder";

  const { t } = useTranslation();
  const dispatch = useDispatch();
  const folderState = useSelector((store) => store.projectFolders);

  const nonePrimary = noneLabelKey ? t(noneLabelKey) : t("folder_selector.none");

  const [projectId, setProjectId] = useState(null);
  const [projectError, setProjectError] = useState(null);

  useEffect(() => {
    if (props.projectId) {
      setProjectId(props.projectId);
      setProjectError(null);
    } else {
      setProjectId(null);
      setProjectError(t("folder_selector.project_required"));
    }
  }, [props.projectId, t]);

  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const searchInputRef = useAutofocusOnOpen(open);

  const [selected, setSelected] = useState({
    id: null,
    field1: nonePrimary,
  });
  const [pendingIds, setPendingIds] = useState(new Set());
  const [focusId, setFocusId] = useState(null);
  const [expanded, setExpanded] = useState(new Set());
  const [editingId, setEditingId] = useState(null);
  const [creatingId, setCreatingId] = useState(null);
  const [draft, setDraft] = useState("");
  const [confirm, setConfirm] = useState(null);
  const [pendingCreateParentId, setPendingCreateParentId] = useState(null);
  const treeScrollRef = useRef(null);

  const scopeTree = useMemo(
    () => buildFolderTree(folderState?.data ?? [], projectId),
    [folderState?.data, projectId]
  );

  useEffect(() => {
    const selectedId = props?.selected?.id;
    if (selectedId) {
      const fromProp =
        props.selected.displayTitle || props.selected.title || null;
      const fromTree = namePath(scopeTree, selectedId).join(" / ") || null;
      setSelected((prev) => ({
        id: selectedId,
        field1:
          fromProp ||
          fromTree ||
          (prev.id === selectedId ? prev.field1 : nonePrimary),
      }));
    } else if (props?.selected === null || props?.selected === undefined) {
      setSelected({ id: null, field1: nonePrimary });
    }
  }, [
    props.selected?.id,
    props.selected?.displayTitle,
    props.selected?.title,
    nonePrimary,
    scopeTree,
  ]);

  const projectTitle = useMemo(() => {
    if (projectTitleProp) return projectTitleProp;
    if (projectProp?.displayTitle || projectProp?.name) {
      return projectProp.displayTitle ?? projectProp.name;
    }
    const row = (folderState?.data ?? []).find(
      (f) => f?.project?.id === projectId
    );
    return row?.project?.displayTitle ?? null;
  }, [projectTitleProp, projectProp, folderState?.data, projectId]);

  const projectInitial = useMemo(() => {
    const t0 = projectTitle?.trim();
    return t0 ? t0.charAt(0).toUpperCase() : "P";
  }, [projectTitle]);

  useEffect(() => {
    if (!open || !scopeTree.length) return;
    setExpanded((prev) => {
      if (prev.size) return prev;
      return new Set(scopeTree.map((n) => n.id));
    });
  }, [open, scopeTree]);

  useEffect(() => {
    if (!open) return;
    setPendingIds(
      new Set(selected?.id != null ? [selected.id] : [])
    );
    setFocusId(selected?.id ?? null);
  }, [open, selected?.id]);

  useEffect(() => {
    if (!editingId) return;
    const tr = treeScrollRef.current;
    const inp = tr?.querySelector(".fs-inline-input, input");
    if (tr && inp) {
      const tb = tr.getBoundingClientRect();
      const ib = inp.getBoundingClientRect();
      if (ib.bottom > tb.bottom - 8) tr.scrollTop += ib.bottom - tb.bottom + 28;
      else if (ib.top < tb.top + 8) tr.scrollTop -= tb.top - ib.top + 28;
    }
  }, [editingId]);

  const searching = query.trim();
  const rows = useMemo(() => {
    if (!searching) {
      return flatten(scopeTree, expanded).map((r) => ({ ...r, matched: true }));
    }
    return searchRows(scopeTree, query) ?? [];
  }, [scopeTree, expanded, searching, query]);

  const spine = useMemo(() => {
    const s = new Set();
    if (focusId) {
      const anc = ancestorPath(scopeTree, focusId) || [];
      anc.forEach((a) => s.add(a));
      s.add(focusId);
    }
    return s;
  }, [scopeTree, focusId]);

  const handleOpen = () => {
    if (props.disabled || !projectId) return;
    setOpen(true);
    setQuery("");
    setEditingId(null);
    setCreatingId(null);
    setConfirm(null);
  };

  const handleClose = () => {
    setOpen(false);
    setQuery("");
    setEditingId(null);
    setCreatingId(null);
    setConfirm(null);
  };

  const applySelection = useCallback(
    (folderId, folderObj) => {
      const display =
        folderObj?.displayTitle ??
        (folderId && scopeTree.length
          ? namePath(scopeTree, folderId).join(" / ")
          : nonePrimary);

      setSelected({
        id: folderId,
        field1: folderId != null ? display : nonePrimary,
      });

      if (values && setFieldValue) setFieldValue(name, folderId);
      else if (onChange) {
        onChange({
          target: { name, value: folderId },
          object: folderObj ?? null,
        });
      }
      setOpen(false);
      setQuery("");
      setEditingId(null);
      setCreatingId(null);
      setConfirm(null);
    },
    [values, setFieldValue, onChange, scopeTree, nonePrimary]
  );

  const handleSelectFolder = (node) => {
    if (String(node.id).startsWith("temp-")) return;
    if (multiSelect) {
      setPendingIds(new Set([node.id]));
      setFocusId(node.id);
      return;
    }
    applySelection(node.id, node.object);
  };

  const handleSelectNone = () => {
    applySelection(null, null);
  };

  const onRowClick = (node, e) => {
    if (String(node.id).startsWith("temp-")) return;
    const id = node.id;
    if (multiSelect) {
      if (e.metaKey || e.ctrlKey) {
        const s = new Set(pendingIds);
        s.has(id) ? s.delete(id) : s.add(id);
        setPendingIds(s);
        setFocusId(id);
        return;
      }
      if (e.shiftKey && focusId != null) {
        const orderedIds = rows.map((r) => r.node.id);
        const a = orderedIds.indexOf(focusId);
        const b = orderedIds.indexOf(id);
        if (a >= 0 && b >= 0) {
          const [lo, hi] = [Math.min(a, b), Math.max(a, b)];
          setPendingIds(new Set(orderedIds.slice(lo, hi + 1)));
        }
        setFocusId(id);
        return;
      }
      const s = new Set(pendingIds);
      s.has(id) ? s.delete(id) : (s.clear(), s.add(id));
      setPendingIds(s);
      setFocusId(id);
      return;
    }
    handleSelectFolder(node);
  };

  const onToggleSelect = (id) => {
    const s = new Set(pendingIds);
    s.has(id) ? s.delete(id) : s.add(id);
    setPendingIds(s);
    setFocusId(id);
  };

  const onToggleExpand = (id) => {
    setExpanded((prev) => {
      const s = new Set(prev);
      s.has(id) ? s.delete(id) : s.add(id);
      return s;
    });
  };

  const fetchItems = useCallback(
    (searchQuery) => {
      if (!projectId) return;
      setLoading(true);
      dispatch(
        getProjectFoldersAsync({
          project_id: projectId,
          query: searchQuery || "",
          page: 1,
          per_page: 500,
          sortBy: "created_at",
          sort: "desc",
        })
      ).finally(() => setLoading(false));
    },
    [dispatch, projectId]
  );

  const debouncedFetch = useCallback(
    debounce((q) => fetchItems(q), 300),
    [fetchItems]
  );

  useEffect(() => {
    if (!open || !projectId) return;
    debouncedFetch(query);
    return () => debouncedFetch.cancel();
  }, [query, open, projectId, debouncedFetch]);

  const refreshFolders = () => fetchItems(query);

  const startCreate = (parentId) => {
    if (!allowManage || !projectId) return;
    const tempId = `temp-${Date.now()}`;
    setCreatingId(tempId);
    setEditingId(tempId);
    setDraft(t("folder_selector.new_folder_default"));
    setFocusId(tempId);
    if (parentId) setExpanded((prev) => new Set(prev).add(parentId));
    setPendingCreateParentId(parentId);
  };

  const onStartRename = (id) => {
    const hit = findNode(scopeTree, id);
    setCreatingId(null);
    setEditingId(id);
    setDraft(hit?.node?.name ?? "");
  };

  const commitEdit = async () => {
    const title = draft.trim();
    const id = editingId;
    if (!id) return;

    if (creatingId === id) {
      if (!title) {
        setEditingId(null);
        setCreatingId(null);
        setDraft("");
        setPendingCreateParentId(null);
        return;
      }
      const action = await dispatch(
        createProjectFolderAsync({
          project_id: projectId,
          title,
          description: "",
          parent_id: pendingCreateParentId,
        })
      );
      if (createProjectFolderAsync.fulfilled.match(action) && action.payload?.id) {
        const result = action.payload;
        await refreshFolders();
        setExpanded((prev) => new Set(prev).add(result.id));
        if (!multiSelect) {
          applySelection(result.id, result);
          return;
        }
        setPendingIds(new Set([result.id]));
        setFocusId(result.id);
      } else {
        toast.error(t("folder_selector.create_failed"));
      }
      setEditingId(null);
      setCreatingId(null);
      setDraft("");
      setPendingCreateParentId(null);
      return;
    }

    if (!title) {
      setEditingId(null);
      setDraft("");
      return;
    }

    const hit = findNode(scopeTree, id);
    if (!hit || hit.node.name === title) {
      setEditingId(null);
      setDraft("");
      return;
    }

    const renameAction = await dispatch(
      updateProjectFolderAsync({
        id,
        folder_id: id,
        project_id: projectId,
        title,
        description: hit.node.object?.description ?? "",
        parent_id: hit.node.object?.parent?.id ?? hit.node.object?.parent_id ?? null,
      })
    );
    if (updateProjectFolderAsync.fulfilled.match(renameAction)) {
      await refreshFolders();
    } else {
      toast.error(t("folder_selector.rename_failed"));
    }
    setEditingId(null);
    setDraft("");
  };

  const cancelEdit = () => {
    setEditingId(null);
    setCreatingId(null);
    setDraft("");
    setPendingCreateParentId(null);
  };

  const askDelete = (id) => {
    const hit = findNode(scopeTree, id);
    if (!hit) return;
    setConfirm({
      id,
      name: hit.node.name,
      count: countDescendants(hit.node),
    });
  };

  const doDelete = async () => {
    if (!confirm || !projectId) return;
    const id = confirm.id;
    const deleteAction = await dispatch(
      deleteProjectFolderAsync({
        id,
        project: { project_id: projectId },
      })
    );
    if (deleteProjectFolderAsync.fulfilled.match(deleteAction)) {
      await refreshFolders();
      if (pendingIds.has(id)) {
        const next = new Set(pendingIds);
        next.delete(id);
        setPendingIds(next);
      }
      if (selected?.id === id) {
        setSelected({ id: null, field1: nonePrimary });
      }
    } else {
      toast.error(t("folder_selector.delete_failed"));
    }
    setConfirm(null);
  };

  const onMove = async (dragId, targetId, pos) => {
    if (!allowManage) return;
    const hit = findNode(scopeTree, dragId);
    if (!hit) return;

    let parentId = null;
    if (pos === "root-end" || targetId === null) {
      parentId = null;
    } else if (pos === "inside") {
      parentId = targetId;
      setExpanded((prev) => new Set(prev).add(targetId));
    } else {
      const th = findNode(scopeTree, targetId);
      parentId = th?.parent?.id ?? null;
    }

    if (targetId !== null && isDescendant(scopeTree, targetId, dragId)) return;

    const currentParent =
      hit.node.object?.parent?.id ?? hit.node.object?.parent_id ?? null;
    if (currentParent === parentId) return;

    const moveAction = await dispatch(
      updateProjectFolderAsync({
        id: dragId,
        folder_id: dragId,
        project_id: projectId,
        title: hit.node.name,
        description: hit.node.object?.description ?? "",
        parent_id: parentId,
      })
    );
    if (updateProjectFolderAsync.fulfilled.match(moveAction)) {
      await refreshFolders();
      setFocusId(dragId);
    } else {
      toast.error(t("folder_selector.move_failed"));
    }
  };

  const confirmFooterSelect = () => {
    if (multiSelect) {
      if (!pendingIds.size) return;
      const firstId = [...pendingIds][0];
      const hit = findNode(scopeTree, firstId);
      applySelection(firstId, hit?.node?.object);
      return;
    }
    if (pendingIds.size === 0) {
      handleSelectNone();
      return;
    }
    const id = [...pendingIds][0];
    const hit = findNode(scopeTree, id);
    applySelection(id, hit?.node?.object);
  };

  const selectedChips = useMemo(() => {
    return [...pendingIds]
      .map((id) => {
        const h = findNode(scopeTree, id);
        return h
          ? { id, name: h.node.name, path: namePath(scopeTree, id) }
          : null;
      })
      .filter(Boolean);
  }, [pendingIds, scopeTree]);

  const focusPath = focusId ? namePath(scopeTree, focusId) : null;

  const hasSelection = selected?.id != null;
  const isDisabled = props.disabled || !projectId;
  const treeLoading = loading || folderState?.loading;
  const searchExpanded = searching
    ? new Set(rows.map((r) => r.node.id))
    : expanded;

  const displayRows = useMemo(() => {
    if (!creatingId || searching) return rows;

    const insertRow = (depth) => ({
      node: {
        id: creatingId,
        name: draft,
        children: [],
        object: null,
      },
      depth,
      hasKids: false,
      matched: true,
    });

    if (pendingCreateParentId == null) {
      return [insertRow(0), ...rows];
    }

    const parentIdx = rows.findIndex((r) => r.node.id === pendingCreateParentId);
    if (parentIdx < 0) return [...rows, insertRow(0)];

    const parentDepth = rows[parentIdx].depth;
    let insertAt = parentIdx + 1;
    while (insertAt < rows.length && rows[insertAt].depth > parentDepth) {
      insertAt += 1;
    }
    const next = [...rows];
    next.splice(insertAt, 0, insertRow(parentDepth + 1));
    return next;
  }, [creatingId, pendingCreateParentId, searching, rows, draft]);

  return (
    <>
      <div>
        <label className="mb-1 block text-sm font-medium text-gray-700">
          {t(label)}
        </label>
        <button
          type="button"
          disabled={isDisabled}
          onClick={handleOpen}
          className={[
            "relative flex w-full items-center justify-between",
            "rounded-md border bg-white px-3 py-2 text-sm shadow-sm",
            "transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500",
            isDisabled
              ? "cursor-not-allowed border-gray-200 text-gray-400"
              : hasSelection
              ? "border-blue-400 text-gray-900 hover:border-blue-500"
              : "border-gray-300 text-gray-500 hover:border-gray-400",
          ].join(" ")}
        >
          <span className="truncate">{selected.field1}</span>
          <ChevronDownIcon className="ml-2 h-4 w-4 shrink-0 text-gray-400" />
        </button>
        {projectError && (
          <p className="mt-1 flex items-center gap-1 text-xs text-amber-600">
            <ExclamationCircleIcon className="h-3.5 w-3.5" />
            {projectError}
          </p>
        )}
        {errors?.[name] && (
          <p className="mt-1 text-xs text-red-600">{errors[name]}</p>
        )}
      </div>

      <PMDialog
        open={open}
        onClose={handleClose}
        size="lg"
        dense
        closeOnOutsideClick
        panelClassName="!max-w-[560px]"
        zIndex={props.zIndex}
      >
        <div className="flex h-full min-h-0 flex-col">
          {/* Header — project scope */}
          <div className="shrink-0 border-b border-gray-200 px-4 pb-3 pt-4">
            <div className="mb-3 flex items-start gap-3">
              <div className="inline-flex min-w-0 items-center gap-2.5 rounded-[11px] border border-gray-200 bg-gray-50 py-1.5 pl-1.5 pr-2.5">
                <span className="grid h-[26px] w-[26px] place-items-center rounded-lg bg-gradient-to-br from-blue-600 to-blue-800 text-xs font-semibold text-white">
                  {projectInitial}
                </span>
                <span className="flex min-w-0 flex-col leading-tight">
                  <span className="truncate text-[13px] font-semibold text-gray-900">
                    {projectTitle ?? t(label)}
                  </span>
                  <span className="text-[10.5px] font-medium uppercase tracking-wider text-gray-500">
                    {t("folder_selector.scope_kind")}
                  </span>
                </span>
              </div>
              <div className="flex-1" />
              <button
                type="button"
                onClick={handleClose}
                className="rounded-lg p-1.5 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600"
                aria-label={t("Close")}
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>

            <div className="flex items-center gap-2.5">
              <label className="flex h-[38px] flex-1 items-center gap-2 rounded-[11px] border border-gray-200 bg-gray-50 px-3 text-gray-400 focus-within:border-blue-500 focus-within:bg-white focus-within:text-gray-500 focus-within:shadow-[0_0_0_4px_rgba(59,130,246,0.12)]">
                <MagnifyingGlassIcon className="h-4 w-4 shrink-0" />
                <input
                  ref={searchInputRef}
                  type="text"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder={t("folder_selector.search_placeholder")}
                  className="min-w-0 flex-1 border-0 bg-transparent text-[13.5px] text-gray-900 outline-none placeholder:text-gray-400"
                />
                {query && (
                  <button
                    type="button"
                    onClick={() => setQuery("")}
                    className="grid place-items-center text-gray-400 hover:text-gray-600"
                  >
                    <XMarkIcon className="h-4 w-4" />
                  </button>
                )}
              </label>
              {allowManage && (
                <button
                  type="button"
                  onClick={() => startCreate(null)}
                  className="inline-flex h-[38px] shrink-0 items-center gap-1.5 whitespace-nowrap rounded-[11px] border border-gray-200 bg-white px-3.5 text-[13px] font-medium text-gray-800 hover:border-gray-300"
                >
                  <FolderIcon className="h-4 w-4" />
                  {t("folder_selector.new_folder")}
                </button>
              )}
            </div>
          </div>

          {/* None option */}
          <div className="shrink-0 border-b border-gray-100 px-2 py-1">
            <button
              type="button"
              onClick={handleSelectNone}
              className={[
                "flex w-full items-center justify-between rounded-lg px-3 py-2.5 text-left text-sm transition-colors",
                selected?.id == null && !pendingIds.size
                  ? "bg-blue-50 font-medium text-blue-700"
                  : "text-gray-700 hover:bg-gray-50",
              ].join(" ")}
            >
              {nonePrimary}
            </button>
          </div>

          {/* Tree body */}
          <div ref={treeScrollRef} className="flex min-h-0 flex-1 flex-col">
            {treeLoading ? (
              <SelectorListSkeleton />
            ) : displayRows.length === 0 ? (
              <div className="px-5 py-10 text-center text-sm text-gray-500">
                {searching
                  ? t("folder_selector.no_match", { query: searching })
                  : t("folder_selector.empty")}
              </div>
            ) : (
              <ProjectFolderTree
                scopeTree={scopeTree}
                rows={displayRows}
                selectedIds={pendingIds}
                focusId={focusId}
                spine={spine}
                expanded={searchExpanded}
                editingId={editingId}
                draft={draft}
                setDraft={setDraft}
                commitEdit={commitEdit}
                cancelEdit={cancelEdit}
                onToggleExpand={onToggleExpand}
                onRowClick={onRowClick}
                onToggleSelect={onToggleSelect}
                onCreateChild={startCreate}
                onStartRename={onStartRename}
                onDelete={askDelete}
                onMove={onMove}
                searching={searching}
                multiSelect={multiSelect}
                allowManage={allowManage}
                t={t}
              />
            )}
          </div>

          {multiSelect && selectedChips.length > 0 && (
            <div className="flex max-h-[76px] shrink-0 flex-wrap gap-1.5 overflow-auto px-4 pb-1">
              {selectedChips.map((n) => (
                <span
                  key={n.id}
                  title={n.path.join(" / ")}
                  className="inline-flex items-center gap-1.5 rounded-full border border-blue-200 bg-blue-50 py-1 pl-2.5 pr-1 text-xs text-gray-900"
                >
                  {n.name}
                  <button
                    type="button"
                    onClick={() => onToggleSelect(n.id)}
                    className="grid h-4 w-4 place-items-center rounded-full text-gray-400 hover:bg-blue-100 hover:text-gray-700"
                  >
                    <XMarkIcon className="h-3 w-3" />
                  </button>
                </span>
              ))}
            </div>
          )}

          {/* Footer */}
          <div className="flex shrink-0 items-center gap-3 border-t border-gray-200 bg-gray-50 px-4 py-3">
            <div className="min-w-0 flex-1 text-xs text-gray-500">
              {multiSelect ? (
                pendingIds.size === 0 ? (
                  t("folder_selector.select_hint")
                ) : (
                  <span>
                    <b className="font-semibold text-gray-900">{pendingIds.size}</b>{" "}
                    {t("folder_selector.selected_count", { count: pendingIds.size })}
                  </span>
                )
              ) : focusPath?.length ? (
                <span className="font-mono text-[11.5px] text-gray-600">
                  {focusPath.join("  ›  ")}
                </span>
              ) : (
                t("folder_selector.select_hint")
              )}
            </div>
            {multiSelect && pendingIds.size > 0 && (
              <button
                type="button"
                onClick={() => setPendingIds(new Set())}
                className="text-xs font-medium text-gray-500 hover:text-gray-800"
              >
                {t("folder_selector.clear")}
              </button>
            )}
            <button
              type="button"
              onClick={handleClose}
              className="inline-flex h-9 items-center rounded-[11px] border border-gray-200 bg-white px-3.5 text-[13px] font-medium text-gray-800 hover:border-gray-300"
            >
              {t("Cancel")}
            </button>
            {multiSelect && (
              <button
                type="button"
                disabled={pendingIds.size === 0}
                onClick={confirmFooterSelect}
                className="inline-flex h-9 items-center rounded-[11px] border border-blue-600 bg-blue-600 px-3.5 text-[13px] font-medium text-white hover:brightness-105 disabled:cursor-not-allowed disabled:opacity-40"
              >
                {t("folder_selector.select")}
                {pendingIds.size > 0 ? ` (${pendingIds.size})` : ""}
              </button>
            )}
          </div>

          {confirm && (
            <div
              className="absolute inset-0 z-[60] grid place-items-center rounded-[20px] bg-gray-900/15"
              onMouseDown={(e) => {
                if (e.target === e.currentTarget) setConfirm(null);
              }}
            >
              <div className="w-[min(330px,88%)] rounded-2xl border border-gray-200 bg-white p-[18px] text-left shadow-xl">
                <h4 className="mb-1.5 text-sm font-semibold text-gray-900">
                  {t("folder_selector.delete_title", { name: confirm.name })}
                </h4>
                <p className="mb-4 text-xs leading-relaxed text-gray-500">
                  {confirm.count > 0
                    ? t("folder_selector.delete_desc_children", {
                        count: confirm.count,
                      })
                    : t("folder_selector.delete_desc")}
                </p>
                <div className="flex justify-end gap-2">
                  <button
                    type="button"
                    onClick={() => setConfirm(null)}
                    className="inline-flex h-9 items-center rounded-[11px] border border-gray-200 bg-white px-3.5 text-[13px] font-medium"
                  >
                    {t("Cancel")}
                  </button>
                  <button
                    type="button"
                    onClick={doDelete}
                    className="inline-flex h-9 items-center rounded-[11px] border border-red-500 bg-red-500 px-3.5 text-[13px] font-medium text-white"
                  >
                    {t("folder_selector.delete")}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </PMDialog>
    </>
  );
};
